import React, { MouseEvent } from "react";
import styled from "styled-components";
import { ColorType, getFontColor } from "../../../configs/constants/ColorType";
import { getFontSize, TextSizeType } from "../../../utils/theme.utils";
import LoadingIndicator from "../../Shared/LoadingIndicator/LoadingIndicator";
import AppIcon, { AppIconType } from "../AppIcon/AppIcon";

export interface ButtonProps extends ButtonStyleProps {
	children?: React.ReactNode;
	props?: any;
	onClick?: (e: MouseEvent<HTMLButtonElement>) => any;
	onSubmit?: any;
	disabled?: boolean;
	form?: string;
	icon?: AppIconType;
	iconSize?: string;
	iconColor?: ColorType;
	inputRef?: any;
	type?: string;

	/**
	 * If to show loading indicator.
	 *
	 * Will also disable button to prevent double taps
	 * i.e. on form submit.
	 */
	loading?: boolean;

	/**
	 * Decreases button padding to become even smaller.
	 */
	lessPadding?: boolean;
}

export interface ButtonStyleProps {
	margin?: string;
	width?: string;
	height?: string;
	backgroundColor?: ColorType;
	textColor?: ColorType;
	fontSize?: TextSizeType;
	hasTextAndIcon?: boolean;
	lessPadding?: boolean;
	border?: string;
}

const Button: React.FC<ButtonProps> = ({
	icon,
	iconSize,
	iconColor,
	border,
	props,
	onClick,
	children,
	onSubmit,
	disabled,
	margin,
	width,
	backgroundColor,
	textColor,
	form,
	height,
	inputRef,
	fontSize,
	loading,
	lessPadding,
	type,
}) => {
	return (
		<ButtonStyles
			ref={inputRef}
			height={height}
			disabled={disabled || loading}
			onSubmit={onSubmit}
			onClick={onClick}
			margin={margin}
			width={width}
			form={form}
			backgroundColor={backgroundColor}
			textColor={textColor}
			hasTextAndIcon={!!icon && !!children}
			lessPadding={lessPadding}
			border={border}
			type={type}
			{...props}
		>
			<ButtonWrapper style={{ fontSize: fontSize ? getFontSize(fontSize) : undefined }}>
				{icon && !loading && (
					<AppIcon margin="0" icon={icon} size={iconSize || "16px"} color={iconColor} />
				)}
				{loading && <LoadingIndicator showText={false} inline size={16} />}
				{children && (
					<div className={`btn-text ${loading || icon ? "has-icon" : ""}`}>{children}</div>
				)}
			</ButtonWrapper>
		</ButtonStyles>
	);
};

export default Button;

const ButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
`;

const ButtonStyles = styled.button<ButtonStyleProps>`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	font-weight: ${(props) => props.theme.fontWeight.FONT_WEIGHT_MEDIUM};
	color: ${(props) => getFontColor(props.textColor, props.theme) || props.theme.colors.COLOR_WHITE};
	background: ${(props) =>
		getFontColor(props.backgroundColor, props.theme) || props.theme.colors.COLOR_ACNE_PRIMARY};
	border: ${(props) => (props.border ? props.border : "none")};
	cursor: pointer;
	padding: ${(props) => {
		if (props.lessPadding) {
			return props.hasTextAndIcon ? "0.35rem 0.5rem 0.35rem 0.4rem" : "0.35rem 0.4rem";
		} else {
			return props.hasTextAndIcon ? "0.6rem 0.9rem 0.6rem 0.7rem" : "0.6rem 0.9rem";
		}
	}};
	border-radius: 6px;
	/* A button label is a single phrase — never let a tight flex row break it
	   across lines (and out of the button's own box) */
	white-space: nowrap;
	margin: ${(props) => (props.margin ? props.margin : 0)};
	width: ${(props) => props.width};
	height: ${(props) => props.height};

	&:hover {
		opacity: 0.9;
	}

	&:disabled {
		cursor: default;
		opacity: 0.6;
	}

	.btn-text.has-icon {
		margin-left: 7px;
	}
`;

/**
 * "Preset" for create buttons
 */
export const CreateButton: React.FC<Omit<ButtonProps, "icon">> = (props) => (
	<Button {...props} icon="create" />
);

/**
 * "Preset" for download buttons
 */
export const DownloadButton: React.FC<Omit<ButtonProps, "icon">> = (props) => (
	<Button {...props} icon="download-white" iconSize="12px" />
);
